import React, { useState, useEffect } from "react";
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Check from '@mui/icons-material/Check';
import StepConnector, { stepConnectorClasses } from '@mui/material/StepConnector';
import {
    Box,
    Divider,
    Paper,
    IconButton,
    Typography
} from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { SaveAtsAgreementTemplateHelper } from "../../../ATS/helper/AtsAgreementHelper";
import { toast } from "react-toastify";
import AgreementPrompt from "./AgreementPrompt";
import AgreementGeneral from "./AgreementGeneral";
import AgreementPreview from "./AgreementPreview";

const QontoConnector = styled(StepConnector)(({ theme }) => ({
    [`&.${stepConnectorClasses.alternativeLabel}`]: {
        top: 10,
        left: 'calc(-50% + 16px)',
        right: 'calc(50% + 16px)',
    },
    [`&.${stepConnectorClasses.active}`]: {
        [`& .${stepConnectorClasses.line}`]: {
            borderColor: '#8b44ff',
        },
    },
    [`&.${stepConnectorClasses.completed}`]: {
        [`& .${stepConnectorClasses.line}`]: {
            borderColor: '#8b44ff',
        },
    },
    [`& .${stepConnectorClasses.line}`]: {
        borderColor: theme.palette.mode === 'dark' ? theme.palette.grey[600] : '#eaeaf0',
        borderTopWidth: 3,
        borderRadius: 1,
    },
}));

const QontoStepIconRoot = styled('div')(({ theme, ownerState }) => ({
    color: theme.palette.mode === 'dark' ? theme.palette.grey[500] : '#eaeaf0',
    display: 'flex',
    height: 22,
    alignItems: 'center',
    ...(ownerState.active && {
        color: '#8b44ff',
    }),
    '& .QontoStepIcon-completedIcon': {
        color: '#8b44ff',
        zIndex: 1,
        fontSize: 18,
    },
    '& .QontoStepIcon-circle': {
        width: 8,
        height: 8,
        borderRadius: '50%',
        backgroundColor: 'currentColor',
    },
}));

function QontoStepIcon(props) {
    const { active, completed, className } = props;

    return (
        <QontoStepIconRoot ownerState={{ active }} className={className}>
            {completed ? (
                <Check className="QontoStepIcon-completedIcon" />
            ) : (
                <div className="QontoStepIcon-circle" />
            )}
        </QontoStepIconRoot>
    );
}

QontoStepIcon.propTypes = {
    active: PropTypes.bool,
    className: PropTypes.string,
    completed: PropTypes.bool,
};

const steps = [
    'Prompt Customization',
    'Agreement Customization',
    'Preview',
];

const AgreementForm = ({ setCurrentMode, data }) => {
    const [activeStep, setActiveStep] = useState(0);
    const [agreementType, setAgreementType] = useState(data?.agreement_type || "");
    const [templateContent, setTemplateContent] = useState(data?.template_content || "");
    const [formData, setFormData] = useState(data || {});

    useEffect(() => {
        if (data) {
            setAgreementType(data.agreement_type || "");
            setTemplateContent(data.template_content || "");
            setFormData(data);
        } else {
            setTemplateContent("");
            setFormData({});
        }
    }, [data]);

    const handleSave = async () => {
        if (!agreementType) {
            toast.error("Agreement Type is required");
            return;
        }

        const payload = {
            // For Joi Schema Validation
            template_pk: formData?.ats_agreement_templates_pk || formData?.id || null,
            template_name: agreementType,
            text_content: templateContent,
            fields: formData?.custom_fields ? Object.assign({}, formData.custom_fields) : null,
            header_value: formData?.header_image_path || null,
            footer_value: formData?.footer_image_path || null,
            id: formData?.ats_agreement_templates_pk || formData?.id || null,
            agreement_type: agreementType,
            template_content: templateContent,
            template_fields: formData?.custom_fields ? Object.assign({}, formData.custom_fields) : null,
            header_image_path: formData?.header_image_path || null,
            footer_image_path: formData?.footer_image_path || null,
            org_name: formData?.org_name || "",
            org_address: formData?.org_address || "",
            authority_name: formData?.authority_name || "",
            authority_title: formData?.authority_title || "",
            authority_place: formData?.authority_place || "",
            default_commission_pct: formData?.default_commission_pct || "",
            default_fee_basis: formData?.default_fee_basis || "",
            default_replacement_months: formData?.default_replacement_months || "",
        };

        return SaveAtsAgreementTemplateHelper(payload).then((res) => {
            if (res && !res.hasError) {
                toast.success("Agreement Template saved successfully");
            } else {
                toast.error(res?.data?.error?.[0]?.message || "Failed to save template");
            }
        }).catch(() => {
            toast.error("An error occurred while saving template");
        });
    };

    function getStepContent(stepIndex) {
        switch (stepIndex) {
            case 0:
                return (
                    <AgreementPrompt
                        setCurrentMode={setCurrentMode}
                        setActiveStep={setActiveStep}
                        activeStep={activeStep}
                        data={formData}
                        setData={setFormData}
                        agreementType={agreementType}
                        setAgreementType={setAgreementType}
                    />
                );
            case 1:
                return (
                    <AgreementGeneral
                        activeStep={activeStep}
                        setActiveStep={setActiveStep}
                        data={formData}
                        setData={setFormData}
                        templateContent={templateContent}
                        setTemplateContent={setTemplateContent}
                    />
                );
            case 2:
                return (
                    <AgreementPreview
                        setCurrentMode={setCurrentMode}
                        activeStep={activeStep}
                        setActiveStep={setActiveStep}
                        data={formData}
                        handleSave={handleSave}
                    />
                );
            default:
                return "Unknown stepIndex";
        }
    }

    return (
        <Paper sx={{ borderRadius: "10px", p: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <IconButton onClick={() => setCurrentMode("grid")} sx={{ mr: 2 }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {data ? "Edit Agreement Template" : "Add Agreement Template"}
                </Typography>
            </Box>

            <Box sx={{ width: '100%' }}>
                <Stack sx={{ width: '100%', mb: 3 }} spacing={2}>
                    <Stepper alternativeLabel activeStep={activeStep} connector={<QontoConnector />}>
                        {steps.map((label) => (
                            <Step key={label}>
                                <StepLabel StepIconComponent={QontoStepIcon}>
                                    {label}
                                </StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                </Stack>
                <Divider sx={{ mb: 3 }} />

                <div>
                    {getStepContent(activeStep)}
                </div>
            </Box>
        </Paper>
    );
};

export default AgreementForm;
